// Commenting: AJAX form submission
//
// Markup
//
//     <form class="js-new-comment-form">
//       <div class="js-comment-form-error" hidden>
//       </div>
//
//       <textarea class="js-comment-field"></textarea>
//
//       <button type="submit">
//         Comment
//       </button>
//     </form>
//
// Primer Validation example:
// see: https://primer.style/css/components/forms#form-group-validation
//
//   <form class="js-new-comment-form">
//     <div class="form-group js-remove-error-state-on-click">
//       <textarea class="js-comment-field" aria-describedby="body-input-validation",
//       </textarea>
//
//       <p class="note error js-comment-form-error" id="body-input-validation" hidden></p>
//
//       <button type="submit">
//         Comment
//       </button>
//     </div>
//   </form>

import {fire, on} from 'delegated-events'
import type {ErrorWithResponse} from '@github-ui/remote-form'
import {changeValue} from '@github-ui/form-utils'
import {
  getCurrentRepositoryNwo,
  getNavigationTabCountUpdateVersion,
  isCurrentRepository,
  updateNavigationTabCountFromResponse,
} from '@github-ui/global-navigation'
import {remoteForm} from '@github-ui/remote-form'
import {replaceContent} from '@github-ui/updatable-content'
import {reportError} from '@github-ui/failbot'

export function updateReactNavigationCounter(
  selector: string,
  html: string,
  expectedUpdateVersion = getNavigationTabCountUpdateVersion('pull-requests'),
  repositoryNwo?: string,
): boolean {
  if (selector !== '#pull-requests-repo-tab-count') return false

  const counter = new DOMParser().parseFromString(html, 'text/html').body.firstElementChild
  const title = counter?.getAttribute('title')
  if (!title) return false

  const numericTitle = title.replace(/\D/g, '')
  if (!numericTitle) return false

  const count = Number(numericTitle)
  if (!Number.isFinite(count)) return false

  const capped = title.trim().endsWith('+')
  const result = updateNavigationTabCountFromResponse(
    'pull-requests',
    {
      type: 'count',
      count: capped ? count + 1 : count,
      exact: !capped,
    },
    expectedUpdateVersion,
    {repositoryNwo},
  )
  return result !== 'rejected'
}

export function updateCommentContent(
  selector: string,
  html: string,
  repositoryNwo?: string,
  pageUrl?: string,
  tabCountUpdateVersion?: number,
): boolean {
  const currentPageUrl = new URL(window.location.href, window.location.origin)
  const responsePageUrl = pageUrl === undefined ? currentPageUrl : new URL(pageUrl, window.location.origin)
  const isCurrentPage =
    responsePageUrl.pathname === currentPageUrl.pathname && responsePageUrl.search === currentPageUrl.search
  if (!isCurrentRepository(repositoryNwo) || !isCurrentPage) return true
  if (updateReactNavigationCounter(selector, html, tabCountUpdateVersion, repositoryNwo)) return true

  const element = document.querySelector(selector)
  if (!(element instanceof HTMLElement)) return false

  replaceContent(element, html)
  return true
}

// Check if element is in viewport
function isInViewport(el: HTMLElement) {
  const rect = el.getBoundingClientRect()
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

// Hide any comment errors on retry.
function clearFormError(form: Element) {
  const formError = form.querySelector('.js-comment-form-error')
  if (formError instanceof HTMLElement) {
    formError.hidden = true
  }
}

// Remove error state when Primer Form Validation enabled field is clicked on
on('click', '.errored.js-remove-error-state-on-click', function ({currentTarget}) {
  currentTarget.classList.remove('errored')
})

// Reset form after AJAX request is successful
remoteForm('.js-new-comment-form', async function (form, wants) {
  const repositoryNwo = getCurrentRepositoryNwo()
  const pageUrl = window.location.href
  const tabCountUpdateVersion = getNavigationTabCountUpdateVersion('pull-requests')
  let response
  clearFormError(form)
  try {
    response = await wants.json()
  } catch (error) {
    // @ts-expect-error catch blocks are bound to `unknown` so we need to validate the type before using it
    const handledConflict = handleFormError(form, error)
    if (!handledConflict) reportError(error)
  }

  if (!response) {
    return
  }

  form.reset()
  for (const field of form.querySelectorAll<HTMLInputElement>('.js-resettable-field')) {
    changeValue(field, field.getAttribute('data-reset-value') || '')
  }
  const writeTab = form.querySelector('.js-write-tab')
  if (writeTab instanceof HTMLElement) {
    const inViewPort = isInViewport(writeTab)
    if (inViewPort) {
      writeTab.click()
    }
  }
  const ref = response.json.updateContent

  for (const selector in ref) {
    const html = ref[selector]
    if (!updateCommentContent(selector, html, repositoryNwo, pageUrl, tabCountUpdateVersion)) {
      // eslint-disable-next-line no-console
      console.warn(`couldn't find ${selector} for immediate update`)
    }
  }

  fire(form, 'comment:success')
})

// Show comment error if ajax request fails.
function handleFormError(form: Element, error: ErrorWithResponse): boolean {
  // eslint-disable-next-line i18n-text/no-en
  let message = 'There was a problem saving your comment.'
  // eslint-disable-next-line i18n-text/no-en
  let messageSuffix = 'Please try again.'
  let handledConflict = false
  if (error.response) {
    if (error.response.status === 409) {
      try {
        const data = error.response.json
        if (typeof data.errors === 'string' && data.errors.length > 0) {
          message = data.errors
          messageSuffix = ''
          handledConflict = true
        }
      } catch {
        handledConflict = false
      }
    } else if (error.response.status === 422) {
      const data = error.response.json
      if (data.errors) {
        if (Array.isArray(data.errors)) {
          message += ` Your comment ${data.errors.join(', ')}.`
        } else {
          message = data.errors
        }
      }
    } else if (error.response.status === 200) {
      // eslint-disable-next-line i18n-text/no-en
      messageSuffix = 'Please reload the page and try again.'
    }
  }

  if (messageSuffix) message += ` ${messageSuffix}`

  const formError = form.querySelector('.js-comment-form-error')
  if (formError instanceof HTMLElement) {
    formError.textContent = message
    formError.hidden = false

    const formGroupValidationFormWrapper = formError.closest('div.form-group.js-remove-error-state-on-click')

    if (formGroupValidationFormWrapper) {
      formGroupValidationFormWrapper.classList.add('errored')
    }
  }

  return handledConflict
}
